import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Breadcrumbs } from 'components/navigation';
import { PageRestricted } from "pages/errors/page-restricted";
import { UserAccess, UserHasAccess } from "components/user-access";
import { 
    DefaultEventTypeChecksModel, DefaultEventTypeCostsModel, DefaultEventTypeDetailsModel, 
    DefaultEventTypeIconModel, DefaultEventTypeProcessModel, DefaultEventTypeScheduleModel, 
    DefaultEventTypeSettingsModel, DefaultEventTypeSummaryModel, FailedOnlyCommentOption,
    FailedOnlyCostOption, FailedOnlySignoffOption, FailedVorRequiredOption, FailedOnlyDefectOption
} from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";
import { Wizard } from "components/wizard";
import { EventTypeDetails } from "pages/fleet/fleet-event-type/shared/EventTypeDetails";
import { EventTypeIcon } from "pages/fleet/fleet-event-type/shared/EventTypeIcon";
import { EventTypeProcess } from "pages/fleet/fleet-event-type/shared/EventTypeProcess";
import { EventTypeSettings } from "pages/fleet/fleet-event-type/shared/EventTypeSettings";
import { EventTypeSchedule } from "pages/fleet/fleet-event-type/shared/EventTypeSchedule";
import { EventTypeCost } from "pages/fleet/fleet-event-type/shared/EventTypeCost";
import { EventTypeChecks } from "pages/fleet/fleet-event-type/shared/EventTypeChecks";
import { EventTypeSummary } from "pages/fleet/fleet-event-type/shared/EventTypeSummary";
import { useDirtyData } from "hooks/useDirtyData";
import { FleetService } from "services";
import { Icon, Intent } from '@blueprintjs/core';
import { AlertUnsavedData, NotificationToaster } from "components/notifications";
import { Button, LinkButton } from "components/buttons";
import { useNavigate } from "react-router-dom";
import { ShowHide } from "components/layout";
import { EventTypeHelpers } from "pages/fleet/fleet-event-type/wizard/shared/EventTypeHelpers";
import { useQuery } from "hooks/useQuery";

export function EventTypeWizard({
    breadcrumbs = []
}) {
    const requiredAction = "FMS:Manage";
    const checksAction = "Checks:Manage";
    const userHasChecksAction = UserHasAccess(checksAction);
    var navigate = useNavigate();
    const eventTypeListingRoute = "/fleet-settings/event-types";
    
    const query = useQuery();
    const cloneId = query.get("cloneId");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasVisitedSummaryPage, setHasVisitedSummaryPage] = useState(false);
    const [forceLoadChildren, setForceLoadChildren] = useState(false);

    const [eventTypeDetailsModel, setEventTypeDetailsModel] = useState(DefaultEventTypeDetailsModel);
    const [eventTypeIconModel, setEventTypeIconModel] = useState(DefaultEventTypeIconModel);
    const [eventTypeProcessModel, setEventTypeProcessModel] = useState(DefaultEventTypeProcessModel);
    const [eventTypeSettingsModel, setEventTypeSettingsModel] = useState(DefaultEventTypeSettingsModel);
    const [eventTypeScheduleModel, setEventTypeScheduleModel] = useState(DefaultEventTypeScheduleModel);
    const [eventTypeCostModel, setEventTypeCostModel] = useState(DefaultEventTypeCostsModel);
    const [eventTypeChecksModel, setEventTypeChecksModel] = useState(DefaultEventTypeChecksModel);
    const [eventTypeSummaryModel, setEventTypeSummaryModel] = useState(DefaultEventTypeSummaryModel);

    const [currentWizardStep, setCurrentWizardStep] = useState(0);
    const [wizardValid, setWizardValid] = useState(false);
    const [canWizardFinish, setCanWizardFinish] = useState(false);
    const [eventTypeCreated, setEventTypeCreated] = useState(false);

    const eventTypeDetailsStep = 0;
    const eventTypeIconStep = 1;
    const eventTypeProcessStep = 2;
    const eventTypeSettingsStep = 3;
    const eventTypeScheduleStep = 4;
    const eventTypeCostStep = 5;
    const eventTypeChecksStep = 6;
    const normalEventTypeSummaryStep = 7;
    // If the user doesn't have checks manage then the summary step is one index earlier.
    const eventTypeSummaryStep = userHasChecksAction ? normalEventTypeSummaryStep : eventTypeChecksStep;

    const [eventTypeDetailsValid, setEventTypeDetailsValid] = useState(false);
    const [eventTypeIconValid, setEventTypeIconValid] = useState(false);
    const [eventTypeProcessValid, setEventTypeProcessValid] = useState(false);
    const [eventTypeSettingsValid, setEventTypeSettingsValid] = useState(false);
    const [eventTypeScheduleValid, setEventTypeScheduleValid] = useState(false);
    const [eventTypeCostValid, setEventTypeCostValid] = useState(false);
    const [eventTypeChecksValid, setEventTypeChecksValid] = useState(false);
    const [specialProcessingKey, setSpecialProcessingKey] = useState(null);
    const [isErMaintenanceLocked, setIsErMaintenanceLocked] = useState(false);

    const areEventTypeDetailsDirty = useDirtyData(DefaultEventTypeDetailsModel, eventTypeDetailsModel);
    const areEventTypeIconDirty = useDirtyData(DefaultEventTypeIconModel, eventTypeIconModel);
    const areEventTypeProcessDirty = useDirtyData(DefaultEventTypeProcessModel, eventTypeProcessModel);
    const areEventTypeSettingsDirty = useDirtyData(DefaultEventTypeSettingsModel, eventTypeSettingsModel);
    const areEventTypeScheduleDirty = useDirtyData(DefaultEventTypeScheduleModel, eventTypeScheduleModel);
    const areEventTypeCostDirty = useDirtyData(DefaultEventTypeCostsModel, eventTypeCostModel);
    const areEventTypeChecksDirty = useDirtyData(DefaultEventTypeChecksModel, eventTypeChecksModel);

    const eventStatusComplete = "complete";

    useEffect(getEventTypeForCloning, 
        [
            cloneId
        ]);
    
    function getEventTypeForCloning() {
        if (cloneId === null || cloneId === undefined || cloneId.trim() === "") {
            setLoading(false);
            return;
        }

        setLoading(true);

        FleetService.getEventType(cloneId)
            .then((response) => {
                setForceLoadChildren(true);

                const constructedModels = EventTypeHelpers.constructAndSetModelFromResponse(
                    response,
                    eventTypeDetailsModel,
                    eventTypeIconModel,
                    eventTypeProcessModel,
                    eventTypeSettingsModel,
                    eventTypeScheduleModel,
                    eventTypeCostModel,
                    eventTypeChecksModel
                );

                const isErLocked = response.isErMaintenanceSendingEnabled === true &&
                    response.specialProcessingKey?.toUpperCase() === 'PMI';

                let lockedProcessModel = constructedModels.process;
                let lockedSettingsModel = constructedModels.settings;

                if (isErLocked) {
                    // ER maintenance accounts enforce a fixed PMI signoff/defect configuration server-side;
                    // force those values here so the locked fields, the summary step and the save payload
                    // all reflect the compliant state rather than the cloned source's values.
                    const lockedModels = EventTypeHelpers.applyErMaintenanceLockedValues(constructedModels.process, constructedModels.settings);
                    lockedProcessModel = lockedModels.process;
                    lockedSettingsModel = lockedModels.settings;
                }

                setEventTypeDetailsModel(prev => { return { ...prev, ...constructedModels.details }; });
                setEventTypeIconModel(prev => { return { ...prev, ...constructedModels.icons }; });
                setEventTypeProcessModel(prev => { return { ...prev, ...lockedProcessModel }; });
                setEventTypeSettingsModel(prev => { return { ...prev, ...lockedSettingsModel }; });
                setEventTypeScheduleModel(prev => { return { ...prev, ...constructedModels.schedule }; });
                setEventTypeCostModel(prev => { return { ...prev, ...constructedModels.costs }; });
                setEventTypeChecksModel(prev => { return { ...prev, ...constructedModels.checks }; });
                setEventTypeSummaryModel(prev => {
                    return {
                        ...prev,
                        details: { ...constructedModels.details },
                        icon: { ...constructedModels.icons },
                        process: { ...lockedProcessModel },
                        settings: { ...lockedSettingsModel },
                        schedule: { ...constructedModels.schedule },
                        costs: { ...constructedModels.costs },
                        checks: { ...constructedModels.checks }
                    };
                });

                setSpecialProcessingKey(response.specialProcessingKey);
                setIsErMaintenanceLocked(isErLocked);
            })
            .catch(() => {
                NotificationToaster.show(Intent.WARNING, 'Event type could not be loaded. Please try again later.');
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const wizardAdditionalInformation = getWizardAdditionalInformation();
    
    function getWizardAdditionalInformation() { 
        let steps = [
            { stepStatus: eventTypeDetailsValid ? eventStatusComplete : "", stepTitle: "Event Type Details" },
            { stepStatus: eventTypeIconValid ? eventStatusComplete : "", stepTitle: "Event Type Icon" },
            { stepStatus: eventTypeProcessValid ? eventStatusComplete : "", stepTitle: "Event Type Process" },
            { stepStatus: eventTypeSettingsValid ? eventStatusComplete : "", stepTitle: "Event Type Settings" },
            { stepStatus: eventTypeScheduleValid ? eventStatusComplete : "", stepTitle: "Event Type Schedule" },
            { stepStatus: eventTypeCostValid ? eventStatusComplete : "", stepTitle: "Event Type Costs" },
            { stepStatus: eventTypeChecksValid ? eventStatusComplete : "", stepTitle: "Event Type Checks" },
            { stepStatus: canWizardFinish ? eventStatusComplete : "", stepTitle: "Event Type Summary" }
        ]

        if (!userHasChecksAction) {
            steps = steps.filter(x => x.stepTitle !== 'Event Type Checks');
        }

        return steps;
    };

    const wizardSteps = getWizardSteps();

    function getWizardSteps() {
        let steps = [
            {
                key: 'step1',
                title: "Event Type Details",
                step: (
                    <EventTypeDetails 
                        model={eventTypeDetailsModel} 
                        saving={saving} 
                        globalLoading={loading}
                        onValidationUpdate={onWizardValidationUpdated}
                        onModelUpdate={onWizardModelUpdated}
                        hasChecksAttached={eventTypeChecksModel.relatedTemplates?.length > 0}
                        forceLoad={forceLoadChildren}
                        onForceLoadFinished={onForceLoadFinished}
                    />
                )
            },
            {
                key: 'step2',
                title: "Event Type Icon",
                step: (
                    <EventTypeIcon
                        model={eventTypeIconModel} 
                        saving={saving} 
                        globalLoading={loading}
                        onValidationUpdate={onWizardValidationUpdated}
                        onModelUpdate={onWizardModelUpdated}
                        forceLoad={forceLoadChildren}
                        onForceLoadFinished={onForceLoadFinished}
                    />
                )
            },
            {
                key: 'step3',
                title: "Event Type Process",
                step: (
                    <EventTypeProcess
                        model={eventTypeProcessModel}
                        saving={saving}
                        globalLoading={loading}
                        onValidationUpdate={onWizardValidationUpdated}
                        onModelUpdate={onWizardModelUpdated}
                        failStatusSettings={hasAnyFailedOnlyOptionsSelected()}
                        forceLoad={forceLoadChildren}
                        onForceLoadFinished={onForceLoadFinished}
                        specialProcessingKey={specialProcessingKey}
                        erMaintenanceLocked={isErMaintenanceLocked}
                    />
                )
            },
            {
                key: 'step4',
                title: "Event Type Settings",
                step: (
                    <EventTypeSettings
                        model={eventTypeSettingsModel}
                        processModel={eventTypeProcessModel}
                        saving={saving}
                        globalLoading={loading}
                        onValidationUpdate={onWizardValidationUpdated}
                        onModelUpdate={onWizardModelUpdated}
                        forceLoad={forceLoadChildren}
                        onForceLoadFinished={onForceLoadFinished}
                        erMaintenanceLocked={isErMaintenanceLocked}
                    />
                )
            },
            {
                key: 'step5',
                title: "Event Type Schedule",
                step: (
                    <EventTypeSchedule
                        model={eventTypeScheduleModel}
                        processModel={eventTypeProcessModel}
                        saving={saving}
                        globalLoading={loading}
                        onValidationUpdate={onWizardValidationUpdated}
                        onModelUpdate={onWizardModelUpdated}
                        forceLoad={forceLoadChildren}
                        onForceLoadFinished={onForceLoadFinished}
                        specialProcessingKey={specialProcessingKey}
                    />
                )
            },
            {
                key: 'step6',
                title: "Event Type Costs",
                step: (
                    <EventTypeCost
                        model={eventTypeCostModel} 
                        processModel={eventTypeProcessModel}
                        saving={saving} 
                        globalLoading={loading}
                        onValidationUpdate={onWizardValidationUpdated}
                        onModelUpdate={onWizardModelUpdated}
                        forceLoad={forceLoadChildren}
                        onForceLoadFinished={onForceLoadFinished}
                    />
                )
            },
            {
                key: 'step7',
                title: "Event Type Checks",
                step: (
                    <EventTypeChecks
                        model={eventTypeChecksModel} 
                        detailsModel={eventTypeDetailsModel}
                        scheduleModel={eventTypeScheduleModel}
                        saving={saving} 
                        globalLoading={loading}
                        onValidationUpdate={onWizardValidationUpdated}
                        onModelUpdate={onWizardModelUpdated}
                        forceLoad={forceLoadChildren}
                        onForceLoadFinished={onForceLoadFinished}
                    />
                )
            },
            {
                key: 'step8',
                title: "Event Type Summary",
                step: (
                    <EventTypeSummary
                        model={eventTypeSummaryModel}
                        saving={saving} 
                        globalLoading={loading}
                        onValidationUpdate={onWizardValidationUpdated}
                        onModelUpdate={onWizardModelUpdated}
                    />
                )
            }
        ];

        if (!userHasChecksAction) {
            steps = steps.filter(x => x.key !== 'step7');
        }
        
        return steps;
    }

    function onWizardChange(step) {
        // if the next step is the summary step and it has not been visited before then set hasVisitedSummaryPage to true
        if (step === eventTypeSummaryStep && hasVisitedSummaryPage === false) {
            setHasVisitedSummaryPage(true);
        }

        setCurrentWizardStep(step);
    }

    const getAdditionalButtons = () => {
        return (
            <Button
                intent="secondary"
                text="Cancel"
                onClick={() => navigate(eventTypeListingRoute)}
                disabled={saving}
                id="cancel-btn"
            />
        );
    }

    useEffect(setIsWizardValid, [
        currentWizardStep,
        eventTypeDetailsValid, 
        areEventTypeDetailsDirty,
        eventTypeIconValid, 
        areEventTypeIconDirty,
        eventTypeProcessValid,
        areEventTypeProcessDirty,
        eventTypeSettingsValid,
        areEventTypeSettingsDirty,
        eventTypeScheduleValid,
        areEventTypeScheduleDirty,
        eventTypeCostValid,
        areEventTypeCostDirty,
        eventTypeChecksValid,
        areEventTypeChecksDirty,
        userHasChecksAction
    ]);

    function setIsWizardValid() {
        const isEventTypeDetailsStepValid = currentWizardStep === eventTypeDetailsStep && eventTypeDetailsValid;
        const isEventTypeIconStepValid = currentWizardStep === eventTypeIconStep && eventTypeIconValid;
        const isEventTypeProcessStepValid = currentWizardStep === eventTypeProcessStep && eventTypeProcessValid;
        const isEventTypeSettingsStepValid = currentWizardStep === eventTypeSettingsStep && eventTypeSettingsValid;
        // if event type is ad-hoc then the schedule step is always considered valid 
        const isEventTypeScheduleStepValid = currentWizardStep === eventTypeScheduleStep && eventTypeScheduleValid;
        const isEventTypeCostStepValid = currentWizardStep === eventTypeCostStep && eventTypeCostValid;
        const isEventTypeChecksStepValid =  currentWizardStep === eventTypeChecksStep && eventTypeChecksValid;
        const isEventTypeSummaryStepValid = currentWizardStep === eventTypeSummaryStep;

        const isValid = isEventTypeDetailsStepValid ||
            isEventTypeIconStepValid ||
            isEventTypeProcessStepValid ||
            isEventTypeSettingsStepValid ||
            isEventTypeScheduleStepValid ||
            isEventTypeCostStepValid ||
            isEventTypeChecksStepValid ||
            isEventTypeSummaryStepValid;

        const canFinish = hasVisitedSummaryPage && 
            eventTypeDetailsValid && 
            eventTypeIconValid && 
            eventTypeProcessValid && 
            eventTypeSettingsValid && 
            eventTypeScheduleValid && 
            eventTypeCostValid && 
            (!userHasChecksAction || eventTypeChecksValid);

        setWizardValid(isValid);
        setCanWizardFinish(canFinish);
    }

    function onWizardValidationUpdated(step, isValid) {
        switch (step) {
            case eventTypeDetailsStep:
            case eventTypeSummaryStep:
                setEventTypeDetailsValid(isValid);
                break;
            case eventTypeIconStep:
                setEventTypeIconValid(isValid);
                break;
            case eventTypeProcessStep:
                setEventTypeProcessValid(isValid);
                break;
            case eventTypeSettingsStep:
                setEventTypeSettingsValid(isValid);
                break;
            case eventTypeScheduleStep:
                setEventTypeScheduleValid(isValid);
                break;
            case eventTypeCostStep:
                setEventTypeCostValid(isValid);
                break;
            case eventTypeChecksStep:
                setEventTypeChecksValid(isValid);
                break;
            default:
                break;
        }
    }

    function onWizardModelUpdated(step, model) {
        const clonedSummaryModel = { ...eventTypeSummaryModel };

        switch (step) {
            case eventTypeDetailsStep:
            case eventTypeSummaryStep: {
                setEventTypeDetailsModel(model);
                clonedSummaryModel["details"] = model;
                break;
            }
            case eventTypeIconStep: {
                setEventTypeIconModel(model);
                clonedSummaryModel["icon"] = model;
                break;
            }
            case eventTypeProcessStep: {
                let cleanedUpModels = { 
                    processModel: model,
                    settingsModel: eventTypeSettingsModel,
                    scheduleModel: eventTypeScheduleModel,
                    costModel: eventTypeCostModel
                };

                // Only apply cleanup to other models in the process model change if fail status is false
                if (model.failStatusEnabled === false) {
                    cleanedUpModels = EventTypeHelpers.cleanupModelsBasedOnProcessModelChanges(
                        model,
                        eventTypeSettingsModel,
                        eventTypeScheduleModel,
                        eventTypeCostModel
                    );
                    
                    setEventTypeSettingsModel(cleanedUpModels.settingsModel);
                    setEventTypeScheduleModel(cleanedUpModels.scheduleModel);
                    setEventTypeCostModel(cleanedUpModels.costModel);

                    clonedSummaryModel["settings"] = cleanSettingsModelForSummaryPage(cleanedUpModels.processModel, eventTypeSettingsModel);
                    clonedSummaryModel["schedule"] = cleanScheduleModelForSummaryPage(cleanedUpModels.processModel, eventTypeScheduleModel);
                    clonedSummaryModel["costs"] = cleanCostModelForSummaryPage(cleanedUpModels.processModel, eventTypeCostModel);
                }

                setEventTypeProcessModel(cleanedUpModels.processModel);
                clonedSummaryModel["process"] = cleanProcessModelForSummaryPage(cleanedUpModels.processModel);
                break;
            }
            case eventTypeSettingsStep: {
                setEventTypeSettingsModel(model);
                clonedSummaryModel["settings"] = cleanSettingsModelForSummaryPage(eventTypeProcessModel, model);
                break;
            }
            case eventTypeScheduleStep: {
                setEventTypeScheduleModel(model);
                clonedSummaryModel["schedule"] = cleanScheduleModelForSummaryPage(eventTypeProcessModel, model);
                break;
            }
            case eventTypeCostStep: {
                setEventTypeCostModel(model);
                clonedSummaryModel["costs"] = cleanCostModelForSummaryPage(eventTypeProcessModel, model);
                break;
            }   
            case eventTypeChecksStep: {
                setEventTypeChecksModel(model);
                clonedSummaryModel["checks"] = model;
                break;
            }
            default:
                break;
        }

        setEventTypeSummaryModel(clonedSummaryModel);
    }

    // Summary page model cleanup
    function cleanProcessModelForSummaryPage(internalProcessModel) {
        const cleanedModel = { ...internalProcessModel };

        // if fail status is not enabled and the fail status option is chosen then "requiresSignoffFailedOnly" and "requiresSignoff" should be defaulted
        if (internalProcessModel.failStatusEnabled === false && internalProcessModel.requiresSignoffValue === FailedOnlySignoffOption.value) {
            cleanedModel.requiresSignoffValue = "1";
            cleanedModel.requiresSignoffFailedOnly = false;
            cleanedModel.requiresSignoff = false;
        }

        // if requires sign-off is false then "signoffHelperText" must be null and "signoffRestricted" should be defaulted
        if (internalProcessModel.requiresSignoff === false && internalProcessModel.requiresSignoffFailedOnly === false) {
            cleanedModel.signoffHelperText = null;
            cleanedModel.signoffRestricted = false;
        } 
        else if (internalProcessModel.requiresSignoffFailedOnly === true) {
            // ensure that if requiresSignoffFailedOnly is true then requiresSignoff must be false
            cleanedModel.requiresSignoff = false;
        }

        return cleanedModel;
    }

    function cleanSettingsModelForSummaryPage(internalProcessModel, internalSettingsModel) {
        const cleanedModel = { ...internalSettingsModel };
    
        // if fail status is not enabled and the fail status option is chosen for comments then reset required comment fields
        if (internalProcessModel.failStatusEnabled === false && internalSettingsModel.requireCommentValue === FailedOnlyCommentOption.value) {
            cleanedModel.requireCommentValue = "1";
            cleanedModel.requiresCommentFailedOnly = false;
            cleanedModel.requiresComment = false
            cleanedModel.commentHelperText = null;
        }

        // if fail status is not enabled and the fail status option is chosen for defects then reset required defect fields
        if (internalProcessModel.failStatusEnabled === false && internalSettingsModel.requireDefectValue === FailedOnlyDefectOption.value) {
            cleanedModel.requireDefectValue = "1";
            cleanedModel.requiresDefectFailedOnly = false;
            cleanedModel.requiresDefect = false
            cleanedModel.defectHelperText = null;
        }

        // if fail status is not enabled and the fail status option is chosen for documentation then reset required documentation fields
        if (internalProcessModel.failStatusEnabled === false && internalSettingsModel.includeDocumentationFailedOnly === true) {
            cleanedModel.requiresDocumentationFailedOnly = false;
        }

        // if comments are not enabled (included) then all comment fields should be defaulted
        if (internalSettingsModel.includeComment === false) {
            cleanedModel.requireCommentValue = "1";
            cleanedModel.requiresComment = false;
            cleanedModel.requiresCommentFailedOnly = false;
            cleanedModel.commentHelperText = null;
        }

        if (internalSettingsModel.requiresCommentFailedOnly === true) {
            cleanedModel.requiresComment = false;
        }

        // if defects are not enabled (included) then all defect fields should be defaulted
        if (internalSettingsModel.includeDefect === false) {
            cleanedModel.requireDefectValue = "1";
            cleanedModel.requiresDefect = false;
            cleanedModel.requiresDefectFailedOnly = false;
            cleanedModel.defectHelperText = null;
        }

        if (internalSettingsModel.requiresDefectFailedOnly === true) {
            cleanedModel.requiresDefect = false;
        }

        // if documentation is not enabled (included) then all documentation fields should be defaulted
        if (internalSettingsModel.includeDocumentation === false) {
            cleanedModel.includeDocumentationFailedOnly = false;
            cleanedModel.requiresDocumentationCount = 0;
            cleanedModel.documentationHelperText = null;
        }

        return cleanedModel;
    }

    function cleanScheduleModelForSummaryPage(internalProcessModel, internalScheduleModel) {
        const cleanedModel = { ...internalScheduleModel };
    
        // if the event is ahhoc then all schedule fields should be defaulted
        if (internalScheduleModel.adHoc === true) {
            cleanedModel.repeatFrequency = null;
            cleanedModel.repeatInterval = null;
            cleanedModel.repeatCount = null;
            cleanedModel.useLocationVisitingHours = false;
            cleanedModel.vorTimer = false;
            cleanedModel.vorTimerFailedOnly = false;
            cleanedModel.vorPreDays = null;
            cleanedModel.vorPostDays = null;
        }

        // if fail status is not enabled and the fail status option is chosen then all affected fields should be defaulted
        if (internalProcessModel.failStatusEnabled === false && internalScheduleModel.vorTimerValue === FailedVorRequiredOption.value) {
            cleanedModel.vorTimerValue = "1";
            cleanedModel.vorTimer = false;
            cleanedModel.vorTimerFailedOnly = false;
            cleanedModel.vorPreDays = null;
            cleanedModel.vorPostDays = null;
        }

        // if vor timer is not enabled/required then all vor fields should be defaulted
        if (internalScheduleModel.vorTimer === false && internalScheduleModel.vorTimerFailedOnly === false) {
            cleanedModel.vorTimerFailedOnly = false;
            cleanedModel.vorPreDays = null;
            cleanedModel.vorPostDays = null;
        }

        return cleanedModel;
    }

    function cleanCostModelForSummaryPage(internalProcessModel, internalCostModel) { 
        const cleanedModel = { ...internalCostModel };

            // if fail status is not enabled then "requiresCosts" and "requiresCostsFailedOnly" should be defaulted
        if (internalProcessModel.failStatusEnabled === false && internalCostModel.requiresCostsValue === FailedOnlyCostOption.value) {
            cleanedModel.requiresCostsValue = "1";
            cleanedModel.requiresCosts = false;
            cleanedModel.requiresCostsFailedOnly = false;

            if ("requiresCostsValue" in eventTypeCostModel) {
                cleanedModel.requiresCostsValue = "1";
            }
        }

        if (internalCostModel.includeCosts === false) {
            cleanedModel.requiresCosts = false;
            cleanedModel.requiresCostsFailedOnly = false;
            cleanedModel.requiresCostsValue = "1";
            cleanedModel.costHelperText = '';
            cleanedModel.includeSuppliers = false;
            cleanedModel.requiresSuppliers = false;
            cleanedModel.includeCostDocumentation = false;
            cleanedModel.requiresCostDocumentationCount = 0;
        }

        if (internalCostModel.requiresCostsFailedOnly === true) {
            cleanedModel.requiresCosts = false;
        }

        return cleanedModel;
    }
    // Summary page model cleanup (END)

    function onWizardFinish() {
        if (!wizardValid) {
            return;
        }

        setSaving(true);

        const eventTypeRequestModel = EventTypeHelpers.getEventTypeRequestModel(
            cloneId || null,
            eventTypeDetailsModel,
            eventTypeIconModel,
            eventTypeProcessModel,
            eventTypeSettingsModel,
            eventTypeScheduleModel,
            eventTypeCostModel,
            eventTypeChecksModel
        );

        FleetService.createEventType(eventTypeRequestModel)
            .then(() => {
                setEventTypeCreated(true);
                NotificationToaster.show(Intent.SUCCESS, 'The event type was created successfully.');
            })
            .catch((error) => {
                const apiError = error || 'The event type could not be created. Please try again later.';

                NotificationToaster.show(Intent.WARNING, apiError);
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function isWizardDirty() {
        return areEventTypeDetailsDirty || 
            areEventTypeIconDirty || 
            areEventTypeProcessDirty || 
            areEventTypeSettingsDirty || 
            (eventTypeDetailsModel.adHoc === false && areEventTypeScheduleDirty) || 
            areEventTypeCostDirty || 
            areEventTypeChecksDirty;
    }

    function resetWizard() {
        setEventTypeCreated(false);

        // Reset models
        setEventTypeDetailsModel(DefaultEventTypeDetailsModel);
        setEventTypeIconModel(DefaultEventTypeIconModel);
        setEventTypeProcessModel(DefaultEventTypeProcessModel);
        setEventTypeSettingsModel(DefaultEventTypeSettingsModel);
        setEventTypeScheduleModel(DefaultEventTypeScheduleModel);
        setEventTypeCostModel(DefaultEventTypeCostsModel);
        setEventTypeChecksModel(DefaultEventTypeChecksModel);
        setEventTypeSummaryModel(DefaultEventTypeSummaryModel);

        // Reset validation states
        setEventTypeDetailsValid(false);
        setEventTypeIconValid(false);
        setEventTypeProcessValid(false);
        setEventTypeSettingsValid(false);
        setEventTypeScheduleValid(false);
        setEventTypeCostValid(false);
        setEventTypeChecksValid(false);

        // Reset wizard navigation state
        setCurrentWizardStep(0);
        setHasVisitedSummaryPage(false);
        setWizardValid(false);
        setCanWizardFinish(false);

        query.delete("cloneId");
    }

    function hasAnyFailedOnlyOptionsSelected() {
        let internalHasAnyFailedOnlyOptionsSelected = false;
        const failStatusSelectedPages = []; 

        if (eventTypeProcessModel.requiresSignoffValue === FailedOnlySignoffOption.value) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Process: event sign-off");
        }

        if (eventTypeSettingsModel.requireCommentValue === FailedOnlyCommentOption.value) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Settings: comments");
        }

        if (eventTypeSettingsModel.requireDefectValue === FailedOnlyDefectOption.value) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Settings: defects");
        }

        if (eventTypeSettingsModel.includeDocumentationFailedOnly === true) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Settings: documentation");
        }

        if (eventTypeScheduleModel.vorTimerValue === FailedVorRequiredOption.value) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Schedule: VOR");
        }

        if (eventTypeCostModel.requiresCostsValue === FailedOnlyCostOption.value) {
            internalHasAnyFailedOnlyOptionsSelected = true;
            failStatusSelectedPages.push("Costs");
        }

        return { hasAnyFailedOnlyOptionsSelected: internalHasAnyFailedOnlyOptionsSelected, failStatusSelectedPages: failStatusSelectedPages };
    }

    function onForceLoadFinished() {
        setForceLoadChildren(false);
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <ShowHide
                    evaluator={!eventTypeCreated}
                    show={(
                        <Fragment>
                            <div className="row">
                                <Breadcrumbs items={breadcrumbs} />
                                <h1>New Event Type</h1>

                                <Wizard 
                                    loading={loading}
                                    onStepChange={onWizardChange}
                                    onFinish={onWizardFinish}
                                    canProceed={wizardValid}
                                    canFinish={canWizardFinish}
                                    showFinishOnAllSteps={true}
                                    disabled={saving}
                                    showStepTitles={true}
                                    additionalStepInformation={wizardAdditionalInformation}
                                    allowDirectStepNavigation={true}
                                    finishButtonText="Create Event Type"
                                    alignButtonsRight={true}
                                    additionalButtonsStart={getAdditionalButtons()}
                                    children={wizardSteps.map(x => 
                                        <div key={x.key} className="spacer-bottom">
                                            <h2>{x.title}</h2>
                                            {x.step}
                                        </div>
                                    )}
                                />
                            </div>

                            <AlertUnsavedData isDirty={isWizardDirty()}/>
                        </Fragment>
                    )}
                    hide={(
                        <div>
                            <div className="spacer-bottom" id="create-success-icon">
                                <Icon icon="tick-circle" size={40} intent={Intent.SUCCESS} />
                            </div>
                            <h2 id="create-success-name">Event type has been added successfully</h2>
                            <div className="spacer-bottom">
                                <p>Your event type <strong>{eventTypeDetailsModel.name}</strong> has been created.</p>
                            </div>
                            <div className="button-row" id="create-success-buttons">
                                <LinkButton intent="primary" text="Event Types" href="/fleet-settings/event-types" />
                                <Button intent="primary" text="Add new event type" id="new-asset" onClick={resetWizard} />
                            </div>
                        </div>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    )
}

EventTypeWizard.propTypes = {
breadcrumbs: PropTypes.array
};